import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useRef, useCallback } from 'react'
import { FlashcardCard } from '@/components/FlashcardCard'
import {
  getDuePreviewFlashcards,
  submitPreviewReview,
  type PreviewFlashcard,
} from '@/lib/previewStorage'
import spanishVerbs from '@/consts/spanish-top-100.json'
import adjectivesData from '@/consts/deepseek_json_20251112_580319.json'
import verbSongs1 from '@/consts/deepseek_json_20251112_8d57c9.json'
import verbSongs2 from '@/consts/deepseek_json_20251112_019866.json'

export const Route = createFileRoute('/preview/flashcards')({
  component: PreviewFlashcardsPage,
})

type AnkiGrade = 0 | 1 | 2 | 3 | 4 | 5

function PreviewFlashcardsPage() {
  // Initialize preview data on mount
  useEffect(() => {
    initializePreviewDataWithDefaults()
  }, [])

  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [reviewStartTime, setReviewStartTime] = useState<number | null>(null)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [sessionStartTime] = useState(Date.now())
  const [flashcards, setFlashcards] = useState<PreviewFlashcard[]>([])
  const [isAnkiInfoExpanded, setIsAnkiInfoExpanded] = useState(false)
  const intervalRef = useRef<number | null>(null)

  // Load flashcards
  useEffect(() => {
    const due = getDuePreviewFlashcards(50)
    setFlashcards(due)
  }, [])

  // Timer effect
  useEffect(() => {
    if (reviewStartTime !== null) {
      intervalRef.current = window.setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - reviewStartTime) / 1000))
      }, 1000)

      return () => {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current)
        }
      }
    }
  }, [reviewStartTime])

  // Reset timer when card changes
  useEffect(() => {
    if (flashcards.length > 0 && currentIndex < flashcards.length) {
      setReviewStartTime(Date.now())
      setElapsedTime(0)
      setIsFlipped(false)
    }
  }, [currentIndex, flashcards.length])

  const currentCard = flashcards.length > 0 ? flashcards[currentIndex] : null

  const handleGrade = useCallback(
    (grade: AnkiGrade) => {
      if (!currentCard) return

      submitPreviewReview(currentCard.id, grade)

      // Reload flashcards
      const due = getDuePreviewFlashcards(50)
      setFlashcards(due)

      // Move to next card
      if (currentIndex < flashcards.length - 1) {
        setCurrentIndex(currentIndex + 1)
      } else {
        setCurrentIndex(0)
      }
    },
    [currentCard, currentIndex, flashcards.length]
  )

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatSessionTime = (startTime: number) => {
    const totalSeconds = Math.floor((Date.now() - startTime) / 1000)
    return formatTime(totalSeconds)
  }

  if (flashcards.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="terminal-border p-8 text-center">
            <h1 className="text-3xl font-bold text-terminal-green mb-4 font-mono">
              [NO CARDS DUE]
            </h1>
            <p className="text-trail-tan font-mono">
              All caught up! No flashcards due for review right now.
            </p>
            <p className="text-sky-blue font-mono text-sm mt-4">
              This is a preview mode. Sign up to save your progress!
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Preview Banner */}
        <div className="mb-4 bg-terminal-green/20 border-2 border-terminal-green p-3 text-center">
          <p className="text-terminal-green font-mono text-sm font-bold">
            [PREVIEW MODE] • Data stored in browser session • Sign up to save progress
          </p>
        </div>

        {/* Header */}
        <div className="mb-4 sm:mb-6 terminal-border p-4 sm:p-6">
          <div className="mb-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
              [FLASHCARD REVIEW]
            </h1>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <div className="text-sm font-mono">
                <span className="text-sky-blue">Card:</span> <span className="text-terminal-green">{formatTime(elapsedTime)}</span>{' '}
                <span className="text-sky-blue">Session:</span> <span className="text-terminal-green">{formatSessionTime(sessionStartTime)}</span>
              </div>
              <div className="text-trail-tan text-sm font-mono">
                Card {currentIndex + 1} of {flashcards.length}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-black border-2 border-sky-blue h-4">
            <div
              className="h-full bg-terminal-green transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / flashcards.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Flashcard */}
        {currentCard && (
          <div className="mb-6">
            <FlashcardCard
              frontSpanish={currentCard.frontSpanish}
              backEnglish={currentCard.backEnglish}
              targetVerb={currentCard.targetVerb}
              isFlipped={isFlipped}
              onFlip={() => setIsFlipped(!isFlipped)}
            />
          </div>
        )}

        {/* Grade Buttons */}
        {isFlipped && currentCard && (
          <div className="mb-6">
            <div className="text-center text-sky-blue font-mono font-bold mb-4 text-sm sm:text-base">
              How well did you know this?
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-4">
              {[
                { grade: 0 as AnkiGrade, label: '0', desc: 'Forgot', color: 'bg-red-600' },
                { grade: 1 as AnkiGrade, label: '1', desc: 'Hard', color: 'bg-orange-600' },
                { grade: 2 as AnkiGrade, label: '2', desc: 'Again', color: 'bg-yellow-600' },
                { grade: 3 as AnkiGrade, label: '3', desc: 'Good', color: 'bg-blue-600' },
                { grade: 4 as AnkiGrade, label: '4', desc: 'Easy', color: 'bg-green-600' },
                { grade: 5 as AnkiGrade, label: '5', desc: 'Perfect', color: 'bg-terminal-green' },
              ].map(({ grade, label, desc, color }) => (
                <button
                  key={grade}
                  onClick={() => handleGrade(grade)}
                  className={`${color} text-white font-bold font-mono text-lg sm:text-xl py-4 sm:py-6 border-4 border-black hover:border-terminal-green transition-all min-h-[44px] sm:min-h-[60px]`}
                >
                  <div className="text-2xl sm:text-3xl mb-1">{label}</div>
                  <div className="text-xs sm:text-sm">{desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Anki Scheduling Info - Collapsible */}
        {currentCard && (
          <AnkiSchedulingInfo 
            card={currentCard} 
            isExpanded={isAnkiInfoExpanded}
            onToggle={() => setIsAnkiInfoExpanded(!isAnkiInfoExpanded)}
          />
        )}

        {/* Card Type Indicator */}
        {currentCard && (
          <div className="text-center text-trail-tan text-xs font-mono">
            Type: <span className="text-sky-blue uppercase">{currentCard.cardType}</span>
            {currentCard.priority !== 5 && (
              <>
                {' '}
                • Priority:{' '}
                <span className="text-terminal-green">{currentCard.priority}/10</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

interface Song {
  title: string
  sections: Array<{
    type: string
    spanish: string
    english: string
  }>
  targetVerb?: string
  sourceVerb?: string
}

// Map verb infinitive to English translation
const verbTranslations: Record<string, string> = {
  abrir: 'to open',
  almorzar: 'to have lunch',
  aprender: 'to learn',
  ayudar: 'to help',
  bailar: 'to dance',
  beber: 'to drink',
  buscar: 'to search',
  caer: 'to fall',
  cambiar: 'to change',
  cantar: 'to sing',
  cerrar: 'to close',
  cocinar: 'to cook',
  comer: 'to eat',
  comprar: 'to buy',
  comprender: 'to understand',
}

function findSongForVerb(verbInfinitive: string, songs: Song[]): Song | null {
  // Try to find a song that matches the verb
  // First try matching by targetVerb (most reliable)
  let song = songs.find((s) => s.targetVerb?.toLowerCase() === verbInfinitive.toLowerCase())
  if (song) return song
  
  // Fallback: match by title (songs have titles like "To Open / Abrir")
  song = songs.find((s) => {
    const titleLower = s.title.toLowerCase()
    return titleLower.includes(`/${verbInfinitive.toLowerCase()}`) || 
           titleLower.endsWith(verbInfinitive.toLowerCase())
  })
  return song || null
}

function getTwoLinesFromSong(song: Song): { spanish: string[]; english: string[] } {
  // Get first two sections (or first two lines if sections have multiple lines)
  const sections = song.sections.slice(0, 2)
  return {
    spanish: sections.map((s) => s.spanish),
    english: sections.map((s) => s.english),
  }
}

function initializePreviewDataWithDefaults() {
  // Check if already initialized - clear old data to ensure fresh start
  if (sessionStorage.getItem('preview_flashcard_decks')) {
    // Clear old data to ensure we use updated translations
    sessionStorage.removeItem('preview_flashcard_decks')
    sessionStorage.removeItem('preview_flashcards')
    sessionStorage.removeItem('preview_flashcard_reviews')
  }

  // Combine songs from both JSON files
  const songs = [...(verbSongs1 as Song[]), ...(verbSongs2 as Song[])]

  // Get first 15 verbs and match them to songs
  const verbFlashcards = spanishVerbs.slice(0, 15).map((verb, idx) => {
    const song = findSongForVerb(verb.infinitive, songs)
    
    if (song) {
      // Use two lines from the song
      const { spanish, english } = getTwoLinesFromSong(song)
      return {
        id: `preview-card-${idx}`,
        deckId: 'preview-deck-1',
        frontSpanish: spanish,
        backEnglish: english,
        cardType: 'lyrics' as const,
        targetVerb: song.targetVerb || verb.infinitive,
        sourceVerb: song.sourceVerb,
        easeFactor: 2.5,
        interval: 0,
        repetitions: 0,
        dueDate: Date.now(),
        priority: 5,
        isActive: true,
        isLearned: false,
        isArchived: false,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
    } else {
      // Fallback: use infinitive with correct translation
      const englishTranslation = verbTranslations[verb.infinitive] || `to ${verb.infinitive}`
      const sourceVerb = englishTranslation.replace(/^to\s+/i, '').trim() // Remove "to " prefix
      return {
        id: `preview-card-${idx}`,
        deckId: 'preview-deck-1',
        frontSpanish: [verb.infinitive],
        backEnglish: [englishTranslation],
        cardType: 'word' as const,
        targetVerb: verb.infinitive,
        sourceVerb: sourceVerb,
        easeFactor: 2.5,
        interval: 0,
        repetitions: 0,
        dueDate: Date.now(),
        priority: 5,
        isActive: true,
        isLearned: false,
        isArchived: false,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
    }
  })

  // Get first 25 adjectives
  const adjectives = (adjectivesData as { adjectives?: Array<{ spanish: string; english: string }> })
    .adjectives?.slice(0, 25) || []

  // Create default deck
  const defaultDeck = {
    id: 'preview-deck-1',
    name: 'Spanish Basics Preview',
    description: 'Demo deck with 15 verbs and 25 adjectives',
    createdAt: Date.now(),
    updatedAt: Date.now(),
  }

  // Create adjective flashcards
  const adjectiveFlashcards = adjectives.map((adj, idx) => ({
    id: `preview-card-${idx + 15}`,
    deckId: defaultDeck.id,
    frontSpanish: [adj.spanish],
    backEnglish: [adj.english],
    cardType: 'word' as const,
    easeFactor: 2.5,
    interval: 0,
    repetitions: 0,
    dueDate: Date.now(),
    priority: 5,
    isActive: true,
    isLearned: false,
    isArchived: false,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  }))

  const flashcards = [...verbFlashcards, ...adjectiveFlashcards]

  // Save to sessionStorage
  sessionStorage.setItem('preview_flashcard_decks', JSON.stringify([defaultDeck]))
  sessionStorage.setItem('preview_flashcards', JSON.stringify(flashcards))
  sessionStorage.setItem('preview_flashcard_reviews', JSON.stringify([]))
}

function AnkiSchedulingInfo({ 
  card, 
  isExpanded, 
  onToggle 
}: { 
  card: {
    easeFactor: number
    interval: number
    repetitions: number
    isLearned: boolean
    lastReviewDate?: number
  }
  isExpanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="mb-6 terminal-border p-4 sm:p-6 bg-black">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left mb-4 hover:opacity-80 transition-opacity"
        type="button"
      >
        <h2 className="text-xl font-bold text-terminal-green font-mono">
          [{isExpanded ? 'ANKI SCHEDULING INFO' : 'VOCAB STATS'}]
        </h2>
        <span className="text-terminal-green font-mono text-lg">
          {isExpanded ? '▼' : '►'}
        </span>
      </button>
      
      {isExpanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-black border-2 border-sky-blue p-3">
            <div className="text-sky-blue text-xs font-mono mb-1 uppercase tracking-wider">
              Ease Factor
            </div>
            <div className="text-terminal-green text-2xl font-bold font-mono">
              {card.easeFactor.toFixed(2)}
            </div>
            <div className="text-trail-tan text-xs font-mono mt-1">Min: 1.3</div>
          </div>
          <div className="bg-black border-2 border-sky-blue p-3">
            <div className="text-sky-blue text-xs font-mono mb-1 uppercase tracking-wider">
              Interval
            </div>
            <div className="text-terminal-green text-2xl font-bold font-mono">
              {card.interval}
            </div>
            <div className="text-trail-tan text-xs font-mono mt-1">
              {card.interval === 0
                ? 'Due now'
                : `${card.interval} day${card.interval !== 1 ? 's' : ''}`}
            </div>
          </div>
          <div className="bg-black border-2 border-sky-blue p-3">
            <div className="text-sky-blue text-xs font-mono mb-1 uppercase tracking-wider">
              Repetitions
            </div>
            <div className="text-terminal-green text-2xl font-bold font-mono">
              {card.repetitions}
            </div>
            <div className="text-trail-tan text-xs font-mono mt-1">Consecutive correct</div>
          </div>
          <div className="bg-black border-2 border-sky-blue p-3">
            <div className="text-sky-blue text-xs font-mono mb-1 uppercase tracking-wider">
              Status
            </div>
            <div
              className={`text-2xl font-bold font-mono ${
                card.isLearned
                  ? 'text-terminal-green'
                  : card.interval > 90
                    ? 'text-terminal-green'
                    : 'text-trail-tan'
              }`}
            >
              {card.isLearned ? 'LEARNED' : 'ACTIVE'}
            </div>
            <div className="text-trail-tan text-xs font-mono mt-1">
              {card.lastReviewDate
                ? `Last: ${new Date(card.lastReviewDate).toLocaleDateString()}`
                : 'New card'}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

